import { FormControl, FormHelperText, FormLabel, Grid, IconButton, Typography } from '@mui/joy';
import { getIn, useFormikContext } from 'formik';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type Address, type AutocompleteOption } from '~/common/types';
import { type CompanyFormValues } from './useAddEditCompanyFormConfig';

import { supportedCountryCodes, useGetPostalCodeCity } from '@/queries';
import useDebounce from '@/shared/hooks/UseDebounce';

import PostalCodeCitySelector, {
  type CitySuggestion,
} from '@/shared/components/addresses/PostalCodeCitySelector';
import { DeleteIcon } from '@/shared/icons/Icons';
import FormikAutocomplete from '@/shared/components/2.0/formik/FormikAutocomplete';
import FormikCountrySelector from '@/shared/components/2.0/formik/FormikCountrySelector';
import FormikTextField from '@/shared/components/2.0/formik/FormikTextField';

const DEBOUNCE_TIMEOUT = 500;

type Props = {
  addressFunctionOptions: AutocompleteOption[];
  address: Address;
  index: number;
  remove: (index: number) => void;
};

const AddressInput: React.FC<Props> = ({ addressFunctionOptions, address, index, remove }) => {
  const { t } = useTranslation(['common', 'errors']);
  const { setFieldValue, setFieldTouched, errors, touched } = useFormikContext<CompanyFormValues>();

  const debouncedCountry = useDebounce(address.country, DEBOUNCE_TIMEOUT);
  const debouncedCity = useDebounce(address.city, DEBOUNCE_TIMEOUT);
  const debouncedPostalCode = useDebounce(address.postalCode, DEBOUNCE_TIMEOUT);

  const addressFilled = (!!debouncedCity || !!debouncedPostalCode) && !!debouncedCountry;

  const { postalCodeCity } = useGetPostalCodeCity(
    {
      countryCode: debouncedCountry ?? '',
      term: debouncedPostalCode ?? '',
    },
    {
      enabled:
        addressFilled &&
        !!debouncedCountry &&
        supportedCountryCodes.includes(debouncedCountry.toUpperCase()),
    },
  );

  const postalCodeMismatch = useMemo(
    () =>
      addressFilled &&
      !!postalCodeCity &&
      !!postalCodeCity.length &&
      !postalCodeCity.some((city) => city.city === debouncedCity),
    [addressFilled, postalCodeCity, debouncedCity],
  );

  const handleSuggestionSelection = (suggestion: CitySuggestion | null) => {
    if (!suggestion) {
      return;
    }

    const { city, postalCode } = suggestion;

    if (city !== address.city) {
      setFieldValue(`addresses[${index}].city`, city);
    }
    if (postalCode !== address.postalCode) {
      setFieldValue(`addresses[${index}].postalCode`, postalCode);
    }
  };

  const hasError = (key: keyof Address) =>
    !!getIn(touched, `addresses[${index}].${key}`) && !!getIn(errors, `addresses[${index}].${key}`);

  return (
    <>
      <Grid
        container
        className={classNames('mb-4', index > 0 && 'mt-4 border-t border-gray-200 pt-2')}
        gap={1}
        xs={12}
      >
        <Grid xs={3}>
          <FormikAutocomplete
            label={t('type')}
            name={`addresses[${index}].function`}
            options={addressFunctionOptions}
          />
        </Grid>

        <Grid xs={3}>
          <FormikCountrySelector
            required
            label={t('country.label')}
            name={`addresses[${index}].country`}
          />
        </Grid>

        <FormikTextField
          required
          className="grow"
          label={t('streetAndNumber')}
          name={`addresses[${index}].addressLine`}
        />

        <span className="mt-6">
          <IconButton color="danger" onClick={() => remove(index)}>
            <DeleteIcon />
          </IconButton>
        </span>
      </Grid>

      <Grid container gap={1} xs={12}>
        <Grid xs={3}>
          <FormControl error={hasError('postalCode')}>
            <FormLabel>{t('postalCode')}</FormLabel>
            <PostalCodeCitySelector
              city={address?.city}
              country={address?.country}
              type="postalCode"
              value={address?.postalCode}
              onBlur={() => setFieldTouched(`addresses[${index}].postalCode`)}
              onChange={handleSuggestionSelection}
              onInputChange={(value) => {
                if (value !== address?.postalCode) {
                  setFieldTouched(`addresses[${index}].postalCode`);
                  setFieldValue(`addresses[${index}].postalCode`, value);
                }
              }}
            />
            {hasError('postalCode') && (
              <FormHelperText className="px-1 text-xs">
                {getIn(errors, `addresses[${index}].postalCode`)}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid xs={3}>
          <FormControl error={hasError('city')}>
            <FormLabel>{t('city')}</FormLabel>
            <PostalCodeCitySelector
              country={address?.country}
              postalCode={address?.postalCode}
              type="city"
              value={address?.city}
              onBlur={() => setFieldTouched(`addresses[${index}].city`)}
              onChange={handleSuggestionSelection}
              onInputChange={(value) => {
                if (value !== address?.city) {
                  setFieldTouched(`addresses[${index}].city`);
                  setFieldValue(`addresses[${index}].city`, value);
                }
              }}
            />
            {hasError('city') && (
              <FormHelperText className="px-1 text-xs">
                {getIn(errors, `addresses[${index}].city`)}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        <FormikTextField label={t('description')} name={`addresses[${index}].description`} />
      </Grid>
      {postalCodeMismatch && (
        <Typography className="pt-2 text-xs" color="warning">
          {t('errors:postalCodeAndCityMismatch')}
        </Typography>
      )}
    </>
  );
};

export default AddressInput;
